import React, { useMemo, useState } from 'react'

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
  Button
} from '@mui/material'

import { IntlService } from 'src/services/IntlService'

interface ClientReportModalProps {
  open: boolean
  onClose: () => void
  params: WeeklyReportFilters
  projectNames: { id: string; name: string }[]
  onConfirm: (opts: {
    reportMode: 'SINGLE_BLOCK' | 'SPLIT_BY_WEEKS'
    includeDevNames: boolean
    splitNonTicketByRow: boolean
    includeTicketName: boolean
  }) => void
}

const ClientReportModal: React.FC<ClientReportModalProps> = ({
  open,
  onClose,
  params,
  projectNames,
  onConfirm
}) => {
  const [reportMode, setReportMode] = useState<
    'SINGLE_BLOCK' | 'SPLIT_BY_WEEKS'
  >('SINGLE_BLOCK')
  const [includeDevNames, setIncludeDevNames] = useState<boolean>(false)
  const [splitNonTicketByRow, setSplitNonTicketByRow] = useState<boolean>(false)
  const [includeTicketName, setIncludeTicketName] = useState<boolean>(false)

  const periodLabel = useMemo(() => {
    const { startDate, endDate } = params || {}

    if (!startDate && !endDate) {
      return ''
    }

    const formattedStart = startDate
      ? IntlService.formatDate(startDate as string, {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : ''
    const formattedEnd = endDate
      ? IntlService.formatDate(endDate as string, {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : ''

    if (formattedStart && formattedEnd) {
      return `${formattedStart} – ${formattedEnd}`
    }

    return formattedStart || formattedEnd
  }, [params])

  const projectsLabel = useMemo(() => {
    const ids = (params?.projectIds || []) as string[]

    if (!ids.length) {
      return 'All projects'
    }

    const idSet = new Set(ids)
    const names = projectNames
      .filter((project) => idSet.has(project.id))
      .map((project) => project.name)

    if (!names.length) {
      return 'All projects'
    }

    return names.join(', ')
  }, [params, projectNames])

  const handleConfirm = () => {
    onConfirm({
      reportMode,
      includeDevNames,
      splitNonTicketByRow,
      includeTicketName
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Client Report</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Period
            </Typography>
            <Typography variant="body1">
              {periodLabel || 'Not specified'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Projects
            </Typography>
            <Typography variant="body1">{projectsLabel}</Typography>
          </Box>

          <Box mt={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Report Mode
            </Typography>
            <RadioGroup
              value={reportMode}
              onChange={(event) =>
                setReportMode(
                  event.target.value as 'SINGLE_BLOCK' | 'SPLIT_BY_WEEKS'
                )
              }
            >
              <FormControlLabel
                value="SINGLE_BLOCK"
                control={<Radio />}
                label="Single Block"
              />
              <FormControlLabel
                value="SPLIT_BY_WEEKS"
                control={<Radio />}
                label="Split by Weeks"
              />
            </RadioGroup>
          </Box>

          <Box mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeDevNames}
                  onChange={(event) => setIncludeDevNames(event.target.checked)}
                />
              }
              label="Include Developer Names"
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={includeTicketName}
                  onChange={(event) =>
                    setIncludeTicketName(event.target.checked)
                  }
                />
              }
              label="Include Ticket Name (Jira only)"
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={splitNonTicketByRow}
                  onChange={(event) =>
                    setSplitNonTicketByRow(event.target.checked)
                  }
                />
              }
              label="Split 'Other' entries by row"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ClientReportModal
